import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, LayoutDashboard, CheckSquare, Folder, Calendar, Users, BarChart3,
  FileText, Settings, User, ChevronDown, Plus, Filter as FilterIcon, Clock,
  Activity, CheckCircle, Flag, Archive, Eye, FileBarChart, UsersRound,
  FolderOpen, Share2, CloudUpload, Shield, Bell, Zap, Lock, MapPin,
  Briefcase, Home, Globe, PoundSterling
} from "lucide-react";

// Softer spring animation curve
const softSpringEasing = "cubic-bezier(0.25, 1.1, 0.4, 1)";

/* ----------------------------- Brand / Logos ----------------------------- */




/* --------------------------------- Avatar -------------------------------- */
function AvatarCircle() {
  return (
    <div className="relative rounded-full shrink-0 size-8 bg-white dark:bg-black border border-neutral-200 dark:border-none">
      <div className="flex items-center justify-center size-8">
        <User size={16} className="text-neutral-900 dark:text-neutral-50" />
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-full border border-neutral-200 dark:border-neutral-800 pointer-events-none"
      />
    </div>
  );
}

/* ------------------------------ Search Input ----------------------------- */
function SearchContainer({ isCollapsed = false, onExpand }) {
  const [searchValue, setSearchValue] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      navigate(`/job-search-browse?q=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const handleIconClick = () => {
    if (isCollapsed && onExpand) {
      onExpand();
      // Small delay to allow transition to start/render
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  return (
    <div
      className={`relative shrink-0 transition-all duration-500 ${isCollapsed ? "w-full flex justify-center" : "w-full"
        }`}
      style={{ transitionTimingFunction: softSpringEasing }}
    >
      <div
        className={`bg-white dark:bg-black h-10 relative rounded-lg flex items-center transition-all duration-500 border border-neutral-200 dark:border-neutral-800 ${isCollapsed ? "w-10 min-w-10 justify-center cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900" : "w-full"
          }`}
        style={{ transitionTimingFunction: softSpringEasing }}
        onClick={handleIconClick}
      >
        <div
          className={`flex items-center justify-center shrink-0 transition-all duration-500 ${isCollapsed ? "p-1" : "px-1"
            }`}
          style={{ transitionTimingFunction: softSpringEasing }}
        >
          <div className="size-8 flex items-center justify-center">
            <Search size={16} className="text-neutral-900 dark:text-neutral-50" />
          </div>
        </div>
        <div
          className={`flex-1 relative transition-opacity duration-500 overflow-hidden ${isCollapsed ? "opacity-0 w-0" : "opacity-100"
            }`}
          style={{ transitionTimingFunction: softSpringEasing }}
        >
          <div className="flex flex-col justify-center size-full">
            <div className="flex flex-col gap-2 items-start justify-center pr-2 py-1 w-full">
              <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e?.target?.value)}
                onKeyDown={handleKeyDown}
                className="w-full bg-transparent border-none outline-none text-[14px] text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 leading-[20px]"
                tabIndex={isCollapsed ? -1 : 0}
                onClick={(e) => e.stopPropagation()} // Prevent triggering parent click if already expanding
              />
            </div>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="absolute inset-0 rounded-lg border border-neutral-200 dark:border-neutral-800 pointer-events-none"
        />
      </div>
    </div>
  );
}

/* --------------------------- Job Search Filters -------------------------- */

// Add this block - Move FilterDropdownHeader outside JobSearchFilters
function FilterDropdownHeader({ icon, label, filterId, hasLock = false, tooltipId, expandedFilters, toggleFilter, isLoggedIn, activeTooltip, setActiveTooltip, handleTooltipToggle }) {
  const isExpanded = expandedFilters?.has(filterId);

  return (
    <div
      className="flex items-center justify-between w-full px-2 py-2 rounded-lg cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
      onClick={() => toggleFilter(filterId)}
    >
      <div className="flex items-center space-x-2">
        {icon}
        <h4 className="text-xs font-medium text-neutral-900 dark:text-neutral-50">{label}</h4>
        {hasLock && !isLoggedIn && (
          <button
            type="button"
            onClick={(e) => {
              e?.stopPropagation();
              handleTooltipToggle(tooltipId);
            }}
            onMouseEnter={() => setActiveTooltip(tooltipId)}
            onMouseLeave={() => setActiveTooltip(null)}
            className="focus:outline-none"
          >
            <Lock size={12} color="#64748B" />
          </button>
        )}
      </div>
      <ChevronDown
        size={16}
        className="text-neutral-900 dark:text-neutral-50 transition-transform duration-300"
        style={{
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
        }}
      />
    </div>
  );
}

function JobSearchFilters({
  selectedFilters,
  onFilterChange,
  onClearAll,
  activeFiltersCount,
  isLoggedIn = false,
  isCollapsed = false
}) {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [expandedFilters, setExpandedFilters] = useState(new Set());

  const locationOptions = [
    'London',
    'South East',
    'South West',
    'Midlands',
    'North West',
    'North East',
    'Yorkshire',
    'Scotland',
    'Wales',
    'Northern Ireland',
    'Remote'
  ];

  const graduateReadinessOptions = [
    'True Graduate Role',
    'Graduate + 1 year experience',
    'Junior (1–2 years)',
    'Not graduate-friendly'
  ];

  const skillMatchOptions = [
    'Direct match (CS, DS, SE)',
    'Related STEM',
    'Any STEM accepted',
    'Degree-agnostic'
  ];

  const visaSupportOptions = [
    'Visa-friendly',
    'UK-only',
    'Unclear'
  ];

  const timeToOfferOptions = [
    'Fast (≤2 weeks)',
    'Average (3–5 weeks)',
    'Slow (6+ weeks)'
  ];

  const remoteHybridOptions = [
    'Remote',
    'Hybrid',
    'On-site'
  ];

  const likelihoodOptions = [
    { value: 'high', label: 'High (70%+)' },
    { value: 'medium', label: 'Medium (40-70%)' },
    { value: 'low', label: 'Low (<40%)' }
  ];

  const tooltipMessages = {
    fitScore: "See your match score for each job based on your profile",
    likelihoodOfInterview: "Discover your chances of getting an interview based on your experience",
    skillGapSize: "Find roles that match your exact skill set"
  };

  const toggleFilter = (filterId) => {
    setExpandedFilters((prev) => {
      const next = new Set(prev);
      if (next?.has(filterId)) next?.delete(filterId);
      else next?.add(filterId);
      return next;
    });
  };

  const handleLocationChange = (location) => {
    const newLocations = selectedFilters?.location?.includes(location)
      ? selectedFilters?.location?.filter(l => l !== location)
      : [...selectedFilters?.location, location];
    onFilterChange('location', newLocations);
  };

  const handleGraduateReadinessChange = (level) => {
    const newLevels = selectedFilters?.graduateReadiness?.includes(level)
      ? selectedFilters?.graduateReadiness?.filter(l => l !== level)
      : [...(selectedFilters?.graduateReadiness || []), level];
    onFilterChange('graduateReadiness', newLevels);
  };

  const handleRemoteHybridChange = (option) => {
    const newOptions = selectedFilters?.remoteHybrid?.includes(option)
      ? selectedFilters?.remoteHybrid?.filter(o => o !== option)
      : [...(selectedFilters?.remoteHybrid || []), option];
    onFilterChange('remoteHybrid', newOptions);
  };

  const handleTooltipToggle = (tooltipId) => {
    if (!isLoggedIn) {
      setActiveTooltip(activeTooltip === tooltipId ? null : tooltipId);
    }
  };

  if (isCollapsed) {
    return null;
  }

  return (
    <div className="space-y-2 w-full">
      {/* Clear Filters Button */}
      {activeFiltersCount > 0 && (
        <div className="px-2 pb-2">
          <button
            onClick={onClearAll}
            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-400 font-medium"
          >
            Clear all ({activeFiltersCount})
          </button>
        </div>
      )}

      {/* Location Filter */}
      <div className="w-full">
        <FilterDropdownHeader
          icon={<MapPin size={14} className="text-neutral-500 dark:text-neutral-400" />}
          label="Location (UK hubs)"
          filterId="location"
          hasLock={false}
          tooltipId={null}
          expandedFilters={expandedFilters}
          toggleFilter={toggleFilter}
          isLoggedIn={isLoggedIn}
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          handleTooltipToggle={handleTooltipToggle}
        />
        {expandedFilters?.has('location') && (
          <div className="space-y-1.5 pl-7 pr-2 py-2">
            {locationOptions?.map((location) => (
              <label key={location} className="flex items-center space-x-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedFilters?.location?.includes(location)}
                  onChange={() => handleLocationChange(location)}
                  className="w-3.5 h-3.5 text-primary-600 border-neutral-300 dark:border-neutral-600 rounded focus:ring-primary-500 bg-white dark:bg-black"
                />
                <span className="text-xs text-neutral-600 dark:text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-50">{location}</span>
              </label>
            ))}
          </div>
        )}
      </div>



      {/* Remote / Hybrid Filter */}
      <div className="w-full">
        <FilterDropdownHeader
          icon={<Home size={14} className="text-neutral-500 dark:text-neutral-400" />}
          label="Remote / Hybrid"
          filterId="remoteHybrid"
          hasLock={false}
          tooltipId={null}
          expandedFilters={expandedFilters}
          toggleFilter={toggleFilter}
          isLoggedIn={isLoggedIn}
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          handleTooltipToggle={handleTooltipToggle}
        />
        {expandedFilters?.has('remoteHybrid') && (
          <div className="space-y-1.5 pl-7 pr-2 py-2">
            {remoteHybridOptions?.map((option) => (
              <label key={option} className="flex items-center space-x-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedFilters?.remoteHybrid?.includes(option)}
                  onChange={() => handleRemoteHybridChange(option)}
                  className="w-3.5 h-3.5 text-primary-600 border-neutral-600 rounded focus:ring-primary-500 bg-black"
                />
                <span className="text-xs text-neutral-300 group-hover:text-neutral-50">{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* NEW: Salary Filter */}
      <div className="w-full border-t border-neutral-200 dark:border-neutral-800 pt-2">
        <FilterDropdownHeader
          icon={<PoundSterling size={14} className="text-neutral-500 dark:text-neutral-400" />} // Reusing briefcase or finding money icon
          label="Salary Range"
          filterId="salary"
          hasLock={false}
          tooltipId={null}
          expandedFilters={expandedFilters}
          toggleFilter={toggleFilter}
          isLoggedIn={isLoggedIn}
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          handleTooltipToggle={handleTooltipToggle}
        />
        {expandedFilters?.has('salary') && (
          <div className="space-y-3 pl-7 pr-2 py-2">
            <div>
              <label className="text-[10px] text-neutral-500 uppercase font-semibold">Min (£)</label>
              <input
                type="number"
                placeholder="e.g. 25000"
                value={selectedFilters?.minSalary || ''}
                onChange={(e) => onFilterChange('minSalary', e.target.value)}
                className="w-full mt-1 bg-white dark:bg-black border border-neutral-300 dark:border-neutral-700 rounded px-2 py-1 text-xs text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="text-[10px] text-neutral-500 uppercase font-semibold">Max (£)</label>
              <input
                type="number"
                placeholder="e.g. 40000"
                value={selectedFilters?.maxSalary || ''}
                onChange={(e) => onFilterChange('maxSalary', e.target.value)}
                className="w-full mt-1 bg-white dark:bg-black border border-neutral-300 dark:border-neutral-700 rounded px-2 py-1 text-xs text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* NEW: Specifics (Keywords, Employer, Role) */}
      <div className="w-full">
        <FilterDropdownHeader
          icon={<Search size={14} className="text-neutral-500 dark:text-neutral-400" />}
          label="Keywords & Employer"
          filterId="specifics"
          hasLock={false}
          tooltipId={null}
          expandedFilters={expandedFilters}
          toggleFilter={toggleFilter}
          isLoggedIn={isLoggedIn}
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          handleTooltipToggle={handleTooltipToggle}
        />
        {expandedFilters?.has('specifics') && (
          <div className="space-y-3 pl-7 pr-2 py-2">
            <div>
              <label className="text-[10px] text-neutral-500 uppercase font-semibold">Keywords</label>
              <input
                type="text"
                placeholder="e.g. React"
                value={selectedFilters?.keywords || ''}
                onChange={(e) => onFilterChange('keywords', e.target.value)}
                className="w-full mt-1 bg-white dark:bg-black border border-neutral-300 dark:border-neutral-700 rounded px-2 py-1 text-xs text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="text-[10px] text-neutral-500 uppercase font-semibold">Employer</label>
              <input
                type="text"
                placeholder="e.g. Google"
                value={selectedFilters?.employer || ''}
                onChange={(e) => onFilterChange('employer', e.target.value)}
                className="w-full mt-1 bg-white dark:bg-black border border-neutral-300 dark:border-neutral-700 rounded px-2 py-1 text-xs text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="text-[10px] text-neutral-500 uppercase font-semibold">Role Type</label>
              <input
                type="text"
                placeholder="e.g. Internship"
                value={selectedFilters?.roleType || ''}
                onChange={(e) => onFilterChange('roleType', e.target.value)}
                className="w-full mt-1 bg-white dark:bg-black border border-neutral-300 dark:border-neutral-700 rounded px-2 py-1 text-xs text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* NEW: Sector */}
      <div className="w-full">
        <FilterDropdownHeader
          icon={<Briefcase size={14} className="text-neutral-500 dark:text-neutral-400" />}
          label="Sector"
          filterId="sector"
          hasLock={false}
          tooltipId={null}
          expandedFilters={expandedFilters}
          toggleFilter={toggleFilter}
          isLoggedIn={isLoggedIn}
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          handleTooltipToggle={handleTooltipToggle}
        />
        {expandedFilters?.has('sector') && (
          <div className="space-y-1.5 pl-7 pr-2 py-2">
            <select
              value={selectedFilters?.sector || ''}
              onChange={(e) => onFilterChange('sector', e.target.value)}
              className="w-full bg-white dark:bg-black border border-neutral-300 dark:border-neutral-700 rounded px-2 py-1 text-xs text-neutral-900 dark:text-neutral-50 focus:outline-none focus:border-primary-500"
            >
              <option value="">Any sector</option>
              {[
                'Technology & Software',
                'Finance & Banking',
                'Consulting',
                'Healthcare & Life Sciences',
                'Engineering & Manufacturing',
                'Digital Marketing',
                'Data Science & Analytics',
                'Creative & Design',
                'Education',
                'Public Sector'
              ].map((sector) => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------- Types / Content Map -------------------------- */
function getSidebarContent(activeSection) {
  const contentMap = {
    dashboard: {
      title: "Dashboard",
      sections: [
        {
          title: "Dashboard Types",
          items: [
            { icon: <Eye size={16} className="text-neutral-50" />, label: "Overview", isActive: true },
            {
              icon: <LayoutDashboard size={16} className="text-neutral-50" />,
              label: "Executive Summary",
              hasDropdown: true,
              children: [
                { label: "Revenue Overview" },
                { label: "Key Performance Indicators" },
                { label: "Strategic Goals Progress" },
                { label: "Department Highlights" },
              ],
            },
            {
              icon: <BarChart3 size={16} className="text-neutral-50" />,
              label: "Operations Dashboard",
              hasDropdown: true,
              children: [
                { label: "Project Timeline" },
                { label: "Resource Allocation" },
                { label: "Team Performance" },
                { label: "Capacity Planning" },
              ],
            },
          ],
        },
        {
          title: "Report Summaries",
          items: [
            {
              icon: <FileBarChart size={16} className="text-neutral-50" />,
              label: "Weekly Reports",
              hasDropdown: true,
              children: [
                { label: "Team Productivity: 87% ↑" },
                { label: "Project Completion: 12/15" },
              ],
            },
          ],
        },
      ],
    },
    tasks: {
      title: "Tasks",
      sections: [
        {
          title: "Quick Actions",
          items: [
            { icon: <Plus size={16} className="text-neutral-50" />, label: "New task" },
            { icon: <FilterIcon size={16} className="text-neutral-50" />, label: "Filter tasks" },
          ],
        },
        {
          title: "My Tasks",
          items: [
            {
              icon: <Clock size={16} className="text-neutral-50" />,
              label: "Due today",
              hasDropdown: true,
              children: [
                { icon: <Flag size={14} className="text-neutral-300" />, label: "Review design mockups" },
                { icon: <CheckCircle size={14} className="text-neutral-300" />, label: "Update documentation" },
              ],
            },
            {
              icon: <Activity size={16} className="text-neutral-50" />,
              label: "In progress",
              hasDropdown: true,
              children: [
                { icon: <CheckSquare size={14} className="text-neutral-300" />, label: "Implement user auth" },
              ],
            },
          ],
        },
        {
          title: "Other",
          items: [
            { icon: <Archive size={16} className="text-neutral-50" />, label: "Archived" },
          ],
        },
      ],
    },
    projects: {
      title: "Projects",
      sections: [
        {
          title: "Quick Actions",
          items: [
            { icon: <Plus size={16} className="text-neutral-50" />, label: "New project" },
            { icon: <FilterIcon size={16} className="text-neutral-50" />, label: "Filter projects" },
          ],
        },
        {
          title: "Active Projects",
          items: [
            {
              icon: <FolderOpen size={16} className="text-neutral-50" />,
              label: "Web Application",
              hasDropdown: true,
              children: [
                { icon: <CheckSquare size={14} className="text-neutral-300" />, label: "Frontend development" },
                { icon: <CheckSquare size={14} className="text-neutral-300" />, label: "API integration" },
              ],
            },
          ],
        },
      ],
    },
    calendar: {
      title: "Calendar",
      sections: [
        {
          title: "Views",
          items: [
            { icon: <Eye size={16} className="text-neutral-50" />, label: "Month view" },
            { icon: <Calendar size={16} className="text-neutral-50" />, label: "Week view" },
            { icon: <Clock size={16} className="text-neutral-50" />, label: "Day view" },
          ],
        },
      ],
    },
    teams: {
      title: "Teams",
      sections: [
        {
          title: "My Teams",
          items: [
            {
              icon: <UsersRound size={16} className="text-neutral-50" />,
              label: "Development Team",
              hasDropdown: true,
              children: [
                { icon: <User size={14} className="text-neutral-300" />, label: "John Doe (Lead)" },
                { icon: <User size={14} className="text-neutral-300" />, label: "Jane Smith" },
              ],
            },
          ],
        },
      ],
    },
    analytics: {
      title: "Analytics",
      sections: [
        {
          title: "Reports",
          items: [
            { icon: <FileBarChart size={16} className="text-neutral-50" />, label: "Performance report" },
            { icon: <BarChart3 size={16} className="text-neutral-50" />, label: "Task completion" },
          ],
        },
      ],
    },
    files: {
      title: "Files",
      sections: [
        {
          title: "Quick Actions",
          items: [
            { icon: <CloudUpload size={16} className="text-neutral-50" />, label: "Upload file" },
            { icon: <Plus size={16} className="text-neutral-50" />, label: "New folder" },
          ],
        },
        {
          title: "Recent Files",
          items: [
            {
              icon: <FileText size={16} className="text-neutral-50" />,
              label: "Recent documents",
              hasDropdown: true,
              children: [
                { icon: <FileText size={14} className="text-neutral-300" />, label: "Project proposal.pdf" },
              ],
            },
            { icon: <Share2 size={16} className="text-neutral-50" />, label: "Shared with me" },
          ],
        },
      ],
    },
    settings: {
      title: "Settings",
      sections: [
        {
          title: "Account",
          items: [
            { icon: <User size={16} className="text-neutral-50" />, label: "Profile settings" },
            { icon: <Shield size={16} className="text-neutral-50" />, label: "Security" },
            { icon: <Bell size={16} className="text-neutral-50" />, label: "Notifications" },
          ],
        },
        {
          title: "Workspace",
          items: [
            {
              icon: <Settings size={16} className="text-neutral-50" />,
              label: "Preferences",
              hasDropdown: true,
              children: [
                { icon: <Eye size={14} className="text-neutral-300" />, label: "Theme settings" },
                { icon: <Clock size={14} className="text-neutral-300" />, label: "Time zone" },
              ],
            },
            { icon: <Zap size={16} className="text-neutral-50" />, label: "Integrations" },
          ],
        },
      ],
    },
  };

  return contentMap?.[activeSection] || contentMap?.tasks;
}

/* ---------------------------- Left Icon Nav Rail -------------------------- */
function IconNavButton({ children, isActive = false, onClick }) {
  return (
    <button
      type="button"
      className={`flex items-center justify-center rounded-lg size-10 min-w-10 transition-colors duration-500
        ${isActive ? "bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50" : "hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"}`}
      style={{ transitionTimingFunction: softSpringEasing }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function IconNavigation({ activeSection, onSectionChange }) {
  const navItems = [
    { id: "dashboard", icon: <LayoutDashboard size={16} />, label: "Dashboard" },
    { id: "tasks", icon: <CheckSquare size={16} />, label: "Tasks" },
    { id: "projects", icon: <Folder size={16} />, label: "Projects" },
    { id: "calendar", icon: <Calendar size={16} />, label: "Calendar" },
    { id: "teams", icon: <Users size={16} />, label: "Teams" },
    { id: "analytics", icon: <BarChart3 size={16} />, label: "Analytics" },
    { id: "files", icon: <FileText size={16} />, label: "Files" },
  ];

  return (
    <aside className="bg-white dark:bg-black flex flex-col gap-2 items-center p-4 w-16 h-full border-r border-neutral-200 dark:border-neutral-800 rounded-l-2xl">
      {/* Logo */}


      {/* Navigation Icons */}
      <div className="flex flex-col gap-2 w-full items-center">
        {navItems?.map((item) => (
          <IconNavButton
            key={item?.id}
            isActive={activeSection === item?.id}
            onClick={() => onSectionChange(item?.id)}
          >
            {item?.icon}
          </IconNavButton>
        ))}
      </div>

      <div className="flex-1" />

      {/* Bottom section */}
      <div className="flex flex-col gap-2 w-full items-center">
        <IconNavButton
          isActive={activeSection === "settings"}
          onClick={() => onSectionChange("settings")}
        >
          <Settings size={16} />
        </IconNavButton>
        <div className="size-8">
          <AvatarCircle />
        </div>
      </div>
    </aside>
  );
}

/* ------------------------------ Right Sidebar ----------------------------- */
function SectionTitle({ title, onToggleCollapse, isCollapsed }) {
  if (isCollapsed) {
    return (
      <div
        className="w-full flex justify-center transition-all duration-500"
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex items-center justify-center rounded-lg size-10 min-w-10 transition-all duration-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          style={{ transitionTimingFunction: softSpringEasing }}
          aria-label="Expand sidebar"
        >
          <span className="inline-block rotate-180">
            <ChevronDown size={16} />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="w-full overflow-hidden transition-all duration-500"
      style={{ transitionTimingFunction: softSpringEasing }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center h-10">
          <div className="px-2 py-1">
            <div className="font-semibold text-[18px] text-neutral-900 dark:text-neutral-50 leading-[27px]">{title}</div>
          </div>
        </div>
        <div className="pr-1">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex items-center justify-center rounded-lg size-10 min-w-10 transition-all duration-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
            style={{ transitionTimingFunction: softSpringEasing }}
            aria-label="Collapse sidebar"
          >
            <ChevronDown size={16} className="-rotate-90" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailSidebar({
  activeSection,
  showFilters = false,
  selectedFilters,
  onFilterChange,
  onClearFilters,
  activeFiltersCount,
  isLoggedIn
}) {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [isCollapsed, setIsCollapsed] = useState(true);
  const content = getSidebarContent(activeSection);

  const toggleExpanded = (itemKey) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next?.has(itemKey)) next?.delete(itemKey);
      else next?.add(itemKey);
      return next;
    });
  };

  const toggleCollapse = () => setIsCollapsed((s) => !s);

  return (
    <aside
      onMouseEnter={() => setIsCollapsed(false)}
      onMouseLeave={() => setIsCollapsed(true)}
      className={`bg-white dark:bg-black flex flex-col gap-4 items-start p-4 rounded-br-2xl transition-all duration-500 h-full ${isCollapsed ? "w-16 min-w-16 !px-0 justify-center" : "w-80"
        }`}
      style={{ transitionTimingFunction: softSpringEasing }}
    >

      <SectionTitle
        title={showFilters ? "Filter" : content?.title}
        onToggleCollapse={toggleCollapse}
        isCollapsed={isCollapsed}
      />
      <SearchContainer isCollapsed={isCollapsed} onExpand={() => setIsCollapsed(false)} />

      <div
        className={`flex flex-col w-full overflow-y-auto transition-all duration-500 flex-1 ${isCollapsed ? "gap-2 items-center" : "gap-4 items-start"
          }`}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        {showFilters ? (
          <JobSearchFilters
            selectedFilters={selectedFilters}
            onFilterChange={onFilterChange}
            onClearAll={onClearFilters}
            activeFiltersCount={activeFiltersCount}
            isLoggedIn={isLoggedIn}
            isCollapsed={isCollapsed}
          />
        ) : (
          content?.sections?.map((section, index) => (
            <MenuSection
              key={`${activeSection}-${index}`}
              section={section}
              expandedItems={expandedItems}
              onToggleExpanded={toggleExpanded}
              isCollapsed={isCollapsed}
            />
          ))
        )}
      </div>

      {!isCollapsed && (
        <div className="w-full mt-auto pt-2 border-t border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-2 px-2 py-2">
            <AvatarCircle />
            <div className="text-[14px] text-neutral-900 dark:text-neutral-50">User Profile</div>
            <button
              type="button"
              className="ml-auto size-8 rounded-md flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-neutral-800"
              aria-label="More"
            >
              <svg className="size-4" viewBox="0 0 16 16" fill="none">
                <circle cx="4" cy="8" r="1" className="fill-neutral-900 dark:fill-neutral-50" />
                <circle cx="8" cy="8" r="1" className="fill-neutral-900 dark:fill-neutral-50" />
                <circle cx="12" cy="8" r="1" className="fill-neutral-900 dark:fill-neutral-50" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}

/* ------------------------------ Menu Elements ---------------------------- */
function MenuItem({ item, isExpanded, onToggle, onItemClick, isCollapsed }) {
  const handleClick = () => {
    if (item?.hasDropdown && onToggle) onToggle();
    else onItemClick?.();
  };

  return (
    <div
      className={`relative shrink-0 transition-all duration-500 ${isCollapsed ? "w-full flex justify-center" : "w-full"
        }`}
      style={{ transitionTimingFunction: softSpringEasing }}
    >
      <div
        className={`rounded-lg cursor-pointer transition-all duration-500 flex items-center relative ${item?.isActive ? "bg-neutral-100 dark:bg-neutral-800" : "hover:bg-neutral-100 dark:hover:bg-neutral-800"
          } ${isCollapsed ? "w-10 min-w-10 h-10 justify-center p-4" : "w-full h-10 px-4 py-2"}`}
        style={{ transitionTimingFunction: softSpringEasing }}
        onClick={handleClick}
        title={isCollapsed ? item?.label : undefined}
      >
        <div className="flex items-center justify-center shrink-0 text-neutral-900 dark:text-neutral-50">{item?.icon}</div>
        <div
          className={`flex-1 relative transition-opacity duration-500 overflow-hidden ${isCollapsed ? "opacity-0 w-0" : "opacity-100 ml-3"
            }`}
          style={{ transitionTimingFunction: softSpringEasing }}
        >
          <div className="text-[14px] text-neutral-900 dark:text-neutral-50 leading-[20px] truncate">{item?.label}</div>
        </div>
        {item?.hasDropdown && (
          <div
            className={`flex items-center justify-center shrink-0 transition-opacity duration-500 ${isCollapsed ? "opacity-0 w-0" : "opacity-100 ml-2"
              }`}
            style={{ transitionTimingFunction: softSpringEasing }}
          >
            <ChevronDown
              size={16}
              className="text-neutral-900 dark:text-neutral-50 transition-transform duration-500"
              style={{
                transitionTimingFunction: softSpringEasing,
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SubMenuItem({ item, onItemClick }) {
  return (
    <div className="w-full pl-9 pr-1 py-[1px]">
      <div
        className="h-10 w-full rounded-lg cursor-pointer transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center px-3 py-1"
        onClick={onItemClick}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[14px] text-neutral-600 dark:text-neutral-300 leading-[18px] truncate">{item?.label}</div>
        </div>
      </div>
    </div>
  );
}

function MenuSection({ section, expandedItems, onToggleExpanded, isCollapsed }) {
  return (
    <div className="flex flex-col w-full">
      <div
        className={`relative shrink-0 w-full transition-all duration-500 overflow-hidden ${isCollapsed ? "h-0 opacity-0" : "h-10 opacity-100"
          }`}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <div className="flex items-center h-10 px-4">
          <div className="text-[14px] text-neutral-500 dark:text-neutral-400">{section?.title}</div>
        </div>
      </div>

      {section?.items?.map((item, index) => {
        const itemKey = `${section?.title}-${index}`;
        const isExpanded = expandedItems?.has(itemKey);

        return (
          <div key={itemKey} className="w-full flex flex-col">
            <MenuItem
              item={item}
              isExpanded={isExpanded}
              onToggle={() => onToggleExpanded(itemKey)}
              onItemClick={() => console.log(`Clicked ${item?.label}`)}
              isCollapsed={isCollapsed}
            />
            {isExpanded && item?.children && !isCollapsed && (
              <div className="flex flex-col gap-1 mb-2">
                {item?.children?.map((child, childIndex) => (
                  <SubMenuItem
                    key={`${itemKey}-${childIndex}`}
                    item={child}
                    onItemClick={() => console.log(`Clicked ${child?.label}`)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------- Layout -------------------------------- */
function TwoLevelSidebar({
  showFilters = false,
  selectedFilters,
  onFilterChange,
  onClearFilters,
  activeFiltersCount,
  isLoggedIn
}) {
  const [activeSection] = useState("dashboard");

  return (
    <div className="flex flex-row h-screen">
      <DetailSidebar
        activeSection={activeSection}
        showFilters={showFilters}
        selectedFilters={selectedFilters}
        onFilterChange={onFilterChange}
        onClearFilters={onClearFilters}
        activeFiltersCount={activeFiltersCount}
        isLoggedIn={isLoggedIn}
      />
    </div>
  );
}

/* ------------------------------- Root Frame ------------------------------ */
export function NavigationSidebar({
  showFilters = false,
  selectedFilters,
  onFilterChange,
  onClearFilters,
  activeFiltersCount = 0,
  isLoggedIn = false
}) {
  return (
    <div className="bg-white dark:bg-[#1a1a1a] h-full flex items-start justify-start">
      <TwoLevelSidebar
        showFilters={showFilters}
        selectedFilters={selectedFilters}
        onFilterChange={onFilterChange}
        onClearFilters={onClearFilters}
        activeFiltersCount={activeFiltersCount}
        isLoggedIn={isLoggedIn}
      />
    </div>
  );
}

export default NavigationSidebar;